import React, { Component } from 'react';

import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import { navigate, RouteComponentProps } from '@reach/router';
import moment from 'moment';
import SignatureCanvas from 'react-signature-canvas';

import { INotificationsActions } from '../../../store/notification/actions';
import { IEventsActions } from '../../../store/player/events/actions';
import { IGamesActions } from '../../../store/player/games/actions';
import { IMessageNotification } from '../../../store/notification/store';
import { IEventsStore } from '../../../store/player/events/store';
import { IGamesStore } from '../../../store/player/games/store';

import BackgroundImage from '../../../components/BackgroundImage';
import Button from '../../../components/Button';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../components/Forms';
import { PX, WrapperSize } from '../../../contrib/ui';
import { withContext } from '../../../contrib/utils';

const background = require('../../../assets/images/placeholder-photo.jpg');

interface IScoreFormData {
  grossScore: string;
  signature: ImageData;
}

interface IProps extends RouteComponentProps {
  actions: {
    events: IEventsActions;
    games: IGamesActions;
    notifications: INotificationsActions;
  };
  events: IEventsStore;
  eventId: string;
  gameId: string;
  games: IGamesStore;
}

class EventPostScoreComponent extends Component<IProps, {}> {
  public canvasRef: React.RefObject<any>;

  constructor(props: IProps) {
    super(props);
    this.canvasRef = React.createRef();
    props.actions.events.get(props.eventId).catch();
    props.actions.games.getAllGames().catch();

    this.submitScore = this.submitScore.bind(this);
  }

  public async submitScore(rawFormData: IScoreFormData): Promise<any> {
    const gameId = Number(this.props.gameId);
    const gameData = {
      grossScore: Number(rawFormData.grossScore),
      id: gameId,
      signature: rawFormData.signature
    };
    const signatureBlob = this.canvasRef.current.getCanvas().toBlob((blob: Blob) => blob);

    const response = await this.props.actions.games.updateGame(
      gameData.id,
      gameData,
      signatureBlob
    );

    if (response.errors) {
      await this.props.actions.notifications.push(({
        status: 'unread',
        text: response.errors.games.msg,
        type: 'error'
      } as unknown) as IMessageNotification);
    } else {
      await this.props.actions.notifications.push(({
        status: 'unread',
        text: 'The score was submitted.',
        type: 'success'
      } as unknown) as IMessageNotification);
      setTimeout(() => navigate('/'), 3000);
    }
  }

  public getCanvas = (): HTMLCanvasElement => this.canvasRef.current.getCanvas();

  public render(): JSX.Element {
    const event = this.props.events[Number(this.props.eventId)];
    return (
      <div className="event-post-score override">
        <div className="wrap">
          {event && (
            <BackgroundImage
              src={event.image || background}
              alt="Event image"
              className="-event-post-score"
            >
              <div className="event-info">
                <IconButton className="info-tooltip-icon override">
                  <InfoIcon color="primary" className="icon override" />
                </IconButton>
                <h1 className="name">{event.name}</h1>
                <p className="date">
                  {`${moment(event.start).format('MMMM D, YYYY')} - `}
                  {`${moment(event.end).format('MMMM D, YYYY')}`}
                </p>
                <p className="course">At {event.club.course.name}</p>
              </div>
            </BackgroundImage>
          )}
          <div className="card override">
            <div className="header">Post your score</div>
            <div className="form-description">
              Enter your total score (gross). If there is a dispute put in your lower score and the
              tournament director can adjust it later.
            </div>
            <FormProvider
              defaultValues={{
                grossScore: '',
                signature: new ImageData(1, 1)
              }}
              onSubmit={this.submitScore}
              validations={{
                grossScore: ['required', 'integer'],
                signature: 'canvasNotEmpty'
              }}
            >
              <FormConsumer>
                {({ values, formActions: { setValue }, errors }: IFormAttributes) => (
                  <div className="card-form">
                    <input
                      className="text-input"
                      id="gross-score"
                      onChange={setValue('grossScore')}
                      type="text"
                      value={values.grossScore}
                    />
                    <label htmlFor="signature" className="label">
                      Player signature
                    </label>
                    <div className="signature" id="signature">
                      <SignatureCanvas
                        canvasProps={{
                          height: 110 * PX,
                          width: WrapperSize - 14 * 6 * PX
                        }}
                        ref={this.canvasRef}
                        onEnd={setValue('signature', { getCanvas: this.getCanvas })}
                      />
                    </div>
                    <Button
                      className="button override"
                      classes={{ disabled: '-disabled' }}
                      disabled={errors.FORM}
                      type="submit"
                    >
                      Submit Score
                    </Button>
                  </div>
                )}
              </FormConsumer>
            </FormProvider>
          </div>
        </div>
      </div>
    );
  }
}

const EventPostScore = withContext(EventPostScoreComponent, 'events', 'games', 'notifications');

export { EventPostScore, EventPostScoreComponent };
